import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Switch,
  Button,
  Typography,
  Space,
  Tabs,
  message,
  Row,
  Col,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Tooltip,
  Popconfirm
} from 'antd';
import {
  SettingOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  ReloadOutlined,
  PlusOutlined,
  KeyOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { apiService } from '../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

interface MediaType {
  media_type_id: string;
  name: string;
  label: string;
  description?: string;
  file_extensions?: string[];
  display_order: string;
  is_visible?: boolean;
}

interface ContentCategory {
  category_id: string;
  name: string;
  label: string;
  description?: string;
  display_order: string;
  is_visible?: boolean;
}

interface APIKey {
  key_id: string;
  provider: string;
  key_name: string;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  last_used_at?: string;
  usage_count: number;
  total_cost: string;
  key_status: string;
  expires_at?: string;
  masked_key: string;
}

const AI_PROVIDERS = [
  { label: 'OpenAI', value: 'openai', color: 'green' },
  { label: 'Anthropic', value: 'anthropic', color: 'blue' },
  { label: 'Google', value: 'google', color: 'orange' },
  { label: 'Local', value: 'local', color: 'purple' }
];

const ConfigManager: React.FC = () => {
  const [mediaTypes, setMediaTypes] = useState<MediaType[]>([]);
  const [contentCategories, setContentCategories] = useState<ContentCategory[]>([]);
  const [apiKeys, setAPIKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [apiKeyModalVisible, setApiKeyModalVisible] = useState(false);
  const [editingKey, setEditingKey] = useState<APIKey | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [mediaTypesData, contentCategoriesData, apiKeysData] = await Promise.all([
        apiService.getMediaTypes(true), // Include hidden items in administration
        apiService.getContentCategories(true), // Include hidden items in administration
        apiService.getAPIKeys()
      ]);
      
      setMediaTypes(mediaTypesData);
      setContentCategories(contentCategoriesData);
      setAPIKeys(apiKeysData);
    } catch (error) {
      console.error('Failed to load configuration:', error);
      message.error('Failed to load configuration data');
    } finally {
      setLoading(false);
    }
  };

  const handleVisibilityToggle = async (type: 'media' | 'category', id: string, visible: boolean) => {
    try {
      // Call the backend API to update visibility
      if (type === 'media') {
        await apiService.updateMediaType(id, { is_visible: visible });
        setMediaTypes(prev => 
          prev.map(item => 
            item.media_type_id === id ? { ...item, is_visible: visible } : item
          )
        );
      } else {
        await apiService.updateContentCategory(id, { is_visible: visible });
        setContentCategories(prev => 
          prev.map(item => 
            item.category_id === id ? { ...item, is_visible: visible } : item
          )
        );
      }
      
      message.success(`${type === 'media' ? 'Media type' : 'Content category'} visibility updated`);
    } catch (error) {
      console.error('Failed to update visibility:', error);
      message.error('Failed to update visibility');
    }
  };

  const handleCreateAPIKey = async (values: any) => {
    try {
      await apiService.createAPIKey({
        provider: values.provider,
        key_name: values.key_name,
        api_key: values.api_key,
        is_default: values.is_default || false,
        expires_at: values.expires_at ? values.expires_at.toISOString() : undefined
      });
      
      message.success('API key created successfully');
      setApiKeyModalVisible(false);
      form.resetFields();
      loadData(); // Reload data to show the new key
    } catch (error) {
      console.error('Failed to create API key:', error);
      message.error('Failed to create API key');
    }
  };

  const handleUpdateAPIKey = async (keyId: string, updates: any) => {
    try {
      await apiService.updateAPIKey(keyId, updates);
      message.success('API key updated successfully');
      loadData();
    } catch (error) {
      console.error('Failed to update API key:', error);
      message.error('Failed to update API key');
    }
  };

  const handleDeleteAPIKey = async (keyId: string) => {
    try {
      await apiService.deleteAPIKey(keyId);
      message.success('API key deleted successfully');
      loadData();
    } catch (error) {
      console.error('Failed to delete API key:', error);
      message.error('Failed to delete API key');
    }
  };

  const handleTestAPIKey = async (keyId: string) => {
    try {
      const result = await apiService.testAPIKey(keyId);
      message.success(`${result.message}`);
      loadData(); // Reload to update last_used_at
    } catch (error) {
      console.error('Failed to test API key:', error);
      message.error('API key test failed');
    }
  };

  const getProviderColor = (provider: string) => {
    const providerInfo = AI_PROVIDERS.find(p => p.value === provider);
    return providerInfo?.color || 'default';
  };

  const mediaTypeColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: MediaType) => (
        <Space>
          <Text strong>{record.label}</Text>
          <Text type="secondary">({text})</Text>
        </Space>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || '-'
    },
    {
      title: 'File Extensions',
      dataIndex: 'file_extensions',
      key: 'file_extensions',
      render: (extensions: string[]) => (
        <Space wrap>
          {extensions?.map(ext => (
            <Tag key={ext} color="blue">{ext}</Tag>
          )) || '-'}
        </Space>
      )
    },
    {
      title: 'Order',
      dataIndex: 'display_order',
      key: 'display_order',
      width: 80
    },
    {
      title: 'Visible',
      dataIndex: 'is_visible',
      key: 'is_visible',
      width: 100,
      render: (visible: boolean, record: MediaType) => (
        <Switch
          checked={visible !== false} // Default to true if undefined
          onChange={(checked) => handleVisibilityToggle('media', record.media_type_id, checked)}
          checkedChildren={<EyeOutlined />}
          unCheckedChildren={<EyeInvisibleOutlined />}
        />
      )
    }
  ];

  const contentCategoryColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: ContentCategory) => (
        <Space>
          <Text strong>{record.label}</Text>
          <Text type="secondary">({text})</Text>
        </Space>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || '-'
    },
    {
      title: 'Order',
      dataIndex: 'display_order',
      key: 'display_order',
      width: 80
    },
    {
      title: 'Visible',
      dataIndex: 'is_visible',
      key: 'is_visible',
      width: 100,
      render: (visible: boolean, record: ContentCategory) => (
        <Switch
          checked={visible !== false} // Default to true if undefined
          onChange={(checked) => handleVisibilityToggle('category', record.category_id, checked)}
          checkedChildren={<EyeOutlined />}
          unCheckedChildren={<EyeInvisibleOutlined />}
        />
      )
    }
  ];

  const apiKeyColumns = [
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      width: 120,
      render: (provider: string) => (
        <Tag color={getProviderColor(provider)}>
          {provider.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Key Name',
      dataIndex: 'key_name',
      key: 'key_name',
      render: (name: string, record: APIKey) => (
        <Space>
          <Text strong>{name}</Text>
          {record.is_default && <Tag color="gold">DEFAULT</Tag>}
        </Space>
      )
    },
    {
      title: 'API Key',
      dataIndex: 'masked_key',
      key: 'masked_key',
      width: 150,
      render: (key: string) => <Text code>{key}</Text>
    },
    {
      title: 'Status',
      dataIndex: 'key_status',
      key: 'key_status',
      width: 100,
      render: (status: string, record: APIKey) => {
        const color = record.is_active ? 'green' : 'red';
        const icon = record.is_active ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />;
        return (
          <Tag color={color} icon={icon}>
            {status.toUpperCase()}
          </Tag>
        );
      }
    },
    {
      title: 'Usage',
      dataIndex: 'usage_count',
      key: 'usage_count',
      width: 80,
      render: (count: number, record: APIKey) => (
        <Tooltip title={`Total cost: $${record.total_cost}`}>
          <Text>{count}</Text>
        </Tooltip>
      )
    },
    {
      title: 'Last Used',
      dataIndex: 'last_used_at',
      key: 'last_used_at',
      width: 120,
      render: (date: string) => date ? dayjs(date).format('MMM DD, YYYY') : 'Never'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_: any, record: APIKey) => (
        <Space>
          <Tooltip title="Test API Key">
            <Button
              type="text"
              icon={<KeyOutlined />}
              onClick={() => handleTestAPIKey(record.key_id)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Toggle Active">
            <Switch
              size="small"
              checked={record.is_active}
              onChange={(checked) => handleUpdateAPIKey(record.key_id, { is_active: checked })}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this API key?"
            onConfirm={() => handleDeleteAPIKey(record.key_id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              icon={<DeleteOutlined />}
              danger
              size="small"
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row align="middle" justify="space-between" style={{ marginBottom: '24px' }}>
        <Col>
          <Title level={2}>
            <SettingOutlined style={{ marginRight: '8px' }} />
            Administration
          </Title>
          <Text type="secondary">
            Manage system configuration, media types, content categories, and API keys
          </Text>
        </Col>
        <Col>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadData}
            loading={loading}
          >
            Refresh
          </Button>
        </Col>
      </Row>

      <Tabs defaultActiveKey="1">
        <TabPane tab="Media Types" key="1">
          <Card>
            <Table
              dataSource={mediaTypes}
              columns={mediaTypeColumns}
              rowKey="media_type_id"
              loading={loading}
              pagination={false}
              size="small"
            />
          </Card>
        </TabPane>
        
        <TabPane tab="Content Categories" key="2">
          <Card>
            <Table
              dataSource={contentCategories}
              columns={contentCategoryColumns}
              rowKey="category_id"
              loading={loading}
              pagination={false}
              size="small"
            />
          </Card>
        </TabPane>

        <TabPane tab="API Keys" key="3">
          <Card
            title={
              <Space>
                <KeyOutlined />
                <span>API Key Management</span>
              </Space>
            }
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setApiKeyModalVisible(true)}
              >
                Add API Key
              </Button>
            }
          >
            <Table
              dataSource={apiKeys}
              columns={apiKeyColumns}
              rowKey="key_id"
              loading={loading}
              pagination={false}
              size="small"
            />
          </Card>
        </TabPane>
      </Tabs>

      {/* Add API Key Modal */}
      <Modal
        title="Add New API Key"
        open={apiKeyModalVisible}
        onCancel={() => {
          setApiKeyModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateAPIKey}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="provider"
                label="AI Provider"
                rules={[{ required: true, message: 'Please select a provider' }]}
              >
                <Select placeholder="Select AI provider">
                  {AI_PROVIDERS.map(provider => (
                    <Option key={provider.value} value={provider.value}>
                      <Tag color={provider.color}>{provider.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="key_name"
                label="Key Name"
                rules={[{ required: true, message: 'Please enter a key name' }]}
              >
                <Input placeholder="e.g., Production OpenAI Key" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="api_key"
            label="API Key"
            rules={[{ required: true, message: 'Please enter the API key' }]}
          >
            <Input.Password placeholder="Enter the API key" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="is_default"
                valuePropName="checked"
              >
                <Switch /> <span style={{ marginLeft: 8 }}>Set as default for this provider</span>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="expires_at"
                label="Expiration Date (Optional)"
              >
                <DatePicker 
                  style={{ width: '100%' }}
                  placeholder="Select expiration date"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default ConfigManager; 